import React, { useState, useEffect, useCallback, useMemo } from "react";
import DetallePago from "./DetallePago";
import swal from "sweetalert";
import printA4 from "../../helpers/A4";
import downprintA4 from "../../helpers/DA4";
import "./styles/Pagos.css";
import {
    FACTURA,
    YAPE,
    BOLETA,
    VALE,
    round,
    EFECTIVO,
    MASTER,
    POS,
    AMERICAN,
    OTROS,
    PAGO_CREDITO,
    RAPPI,
    PLIN,
    decimalAdjust,
    CONTADO,
    convertStrToJson,
} from "../../Global";
import EfectivoImg from "./images/efectivo.png";
import Mastercard from "./images/mastercard.png";
import American from "./images/american.png";
import OtrosPagos from "./images/otros.png";
import Credito from "./images/credito.png";
import Cliente from "./images/cliente.png";
import Yape from "./images/yape.png";
import Plin from "./images/plin logo.png";
import Rappi from "./images/rappi logo.png";
import Pos from "./images/pos-icon.png";
import ReactTable from "react-table";
import "react-table/react-table.css";
import matchSorter from "match-sorter";
import { BotonGestionPreventas, BotonNuevaPreventa } from "../../componentes/Plantillas/Botones";
import { GuiaRemisionSelect } from "./GuiaRemisionSelect";
import Modal from "../../componentes/clientes/ClienteModal";
import { notificarMsg } from "../../componentes/Almacenes/AlmacenNotify";

const DEFAULT_VALIDATION_CODE = 'XXXXXXXX'

// Constantes centralizadas
const PAYMENT_METHODS_TEXT = {
    [YAPE]: "Yape",
    [POS]: "POS",
    [OTROS]: "Otros",
    [PAGO_CREDITO]: "Crédito",
    [PLIN]: "Plin",
    [RAPPI]: "Rappi",
    [MASTER]: "Mastercard",
    [AMERICAN]: "American Express",
};

const EXCEED_BALANCE_ALLOWED_TYPES = [EFECTIVO];

// Configuración de métodos de pago para renderizado
const PAYMENT_METHODS_CONFIG = {
    [EFECTIVO]: {
        src: EfectivoImg,
        className: "btn-outline-success",
        title: "Efectivo",
        requiresValidation: false
    },
    [POS]: {
        src: Pos,
        className: "btn-outline-secondary",
        title: "POS",
        requiresValidation: false
    },
    [YAPE]: {
        src: Yape,
        className: "btn-yape",
        title: "Yape",
        requiresValidation: false
    },
    [MASTER]: {
        src: Mastercard,
        className: "btn-outline-danger",
        title: "Mastercard",
        requiresValidation: false
    },
    [AMERICAN]: {
        src: American,
        className: "btn-outline-info",
        title: "American Express",
        requiresValidation: false
    },
    [OTROS]: {
        src: OtrosPagos,
        className: "btn-outline-warning",
        title: "Otros métodos de pago",
        requiresValidation: true,
        permission: 'pago-otros'
    },
    [PAGO_CREDITO]: {
        src: Credito,
        className: "btn-outline-danger",
        title: "Crédito",
        requiresValidation: true,
        permission: 'vender-a-credito'
    },
    [PLIN]: {
        src: Plin,
        className: "btn-outline-info",
        title: "Plin",
        requiresValidation: false
    },
    [RAPPI]: {
        src: Rappi,
        className: "btn-outline-warning",
        title: "Rappi",
        requiresValidation: false
    }
};

const PagosUI = props => {
    // Estados
    const [showNuevaPreventa, setShowNuevaPreventa] = useState(false);
    const [showModalValidacion, setShowModalValidacion] = useState(false);
    const [modalValidacionTipoPago, setModalValidacionTipoPago] = useState(null);
    const [codigosValidacionUsuario, setCodigosValidacionUsuario] = useState([]);
    const [typedCodigoUsuario, setTypedCodigoUsuario] = useState("");
    const [confEmpresa, setConfEmpresa] = useState({ showExactChange: false });

    const [showModalWhatsApp, setShowModalWhatsApp] = useState(false);
    const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
    const [whatsAppNumber, setWhatsAppNumber] = useState("");
    const [whatsAppPlaceholder, setWhatsAppPlaceholder] = useState("Ingrese 9 dígitos");
    const whatsAppInputRef = React.useRef(null);

    // Estados de Errores
    const [localError, setLocalError] = useState(null);
    const [showErrorAlert, setShowErrorAlert] = useState(false);

    // Cálculos memoizados
    const saldoRestante = useMemo(() => {
        return round(props.Total - props.TotalPagado, 2);
    }, [props.Total, props.TotalPagado]);

    const vueltoCalculado = useMemo(() => {
        if (!props.Vuelto) return "0.00";

        if (!confEmpresa.showExactChange) {
            const vuelto = props.TotalPagado - props.TotalRedondeado;
            return vuelto ? vuelto.toLocaleString("en", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) : "0.00";
        }

        return decimalAdjust("floor", props.Vuelto, -2);
    }, [props.Vuelto, props.TotalPagado, props.TotalRedondeado, confEmpresa]);

    const clienteVarios = useMemo(() => {
        return props.preventa.NroTipoDocumento === "99999999";
    }, [props.preventa.NroTipoDocumento]);

    const hasCreditPayment = useMemo(() => {
        return props.Pagos.some(p => p.IdTipoPago === PAGO_CREDITO);
    }, [props.Pagos]);

    const lastPaymentIsContado = useMemo(() => {
        return props.Pagos.length > 0 && props.Pagos[props.Pagos.length - 1].IdTipoPago === CONTADO;
    }, [props.Pagos]);

    const disableCredit = useMemo(() => {
        return clienteVarios || hasCreditPayment;
    }, [clienteVarios, hasCreditPayment]);

    const disableAll = useMemo(() => {
        if (props.Pagos.length === 0) return false;
        if (lastPaymentIsContado) return true;

        const isCompleted = saldoRestante <= 0;
        const hasExceedablePayment = props.Pagos.some(p =>
            EXCEED_BALANCE_ALLOWED_TYPES.includes(p.IdTipoPago)
        );

        return isCompleted && !hasExceedablePayment;
    }, [props.Pagos.length, lastPaymentIsContado, saldoRestante, props.Pagos]);

    const shouldRemovePayments = useMemo(() => {
        if (!Array.isArray(props.Pagos) || props.Pagos.length === 0) {
            return false;
        }

        const total = props.Total;
        let accumulated = 0;
        let hasNegativeBalance = false;

        for (let i = 0; i < props.Pagos.length; i++) {
            const pago = props.Pagos[i];
            accumulated += pago.Monto;

            if (accumulated > total && i < props.Pagos.length - 1) {
                if (!EXCEED_BALANCE_ALLOWED_TYPES.includes(pago.IdTipoPago)) {
                    hasNegativeBalance = true;
                    break;
                }
            }
        }

        return hasNegativeBalance;
    }, [props.Pagos, props.Total]);

    // Callbacks optimizados
    const fetchConfEmpresa = useCallback(async () => {
        try {
            const req = await fetch(`/api/config/emp`);
            
            if (!req.ok) {
                throw new Error(`Error HTTP ${req.status}: No se pudo cargar la configuración de empresa`);
            }
            
            const conf = await req.json();

            if (!('showExactChange' in conf)) {
                conf['showExactChange'] = false;
            }

            return conf;
        } catch (e) {
            console.error("Error fetching empresa config:", e);
            // No mostrar error aquí porque es operación secundaria, solo retornar defaults
            return { showExactChange: false };
        }
    }, []);

    const fetchCodigosValidacion = useCallback(async () => {
        try {
            const req = await fetch('/api/usuarios/codigoConfirmacion/sucursal');

            if (!req.ok) {
                throw new Error(`Error HTTP ${req.status}: No se pudieron obtener los códigos de validación`);
            }

            const res = await req.json();
            
            if (!Array.isArray(res)) {
                throw new Error("Formato inesperado de códigos de validación");
            }
            
            return res.map(c => c.Codigo);
        } catch (e) {
            console.error("Error fetching validation codes:", e);
            // Retornar default pero sin mostrar error al usuario (es secundario)
            return [DEFAULT_VALIDATION_CODE];
        }
    }, []);

    // Helpers para manejo de errores
    const handleError = useCallback((errorObj, context = "") => {
        let errorMessage = "Ha ocurrido un error inesperado";
        let errorDetails = "";
        let errorCode = "UNKNOWN";

        // Capturar diferentes tipos de errores
        if (typeof errorObj === 'string') {
            errorMessage = errorObj;
        } else if (errorObj instanceof Error) {
            errorMessage = errorObj.message || "Error desconocido";
            errorDetails = errorObj.stack?.split('\n')[0] || "";
        } else if (errorObj?.message) {
            errorMessage = errorObj.message;
            errorCode = errorObj.code || errorObj.status || "UNKNOWN";
        }

        const fullError = {
            message: errorMessage,
            details: errorDetails,
            context: context,
            code: errorCode,
            timestamp: new Date().toISOString()
        };

        // Log para debugging
        console.error(`[${context}] Error:`, fullError);
        
        setLocalError(fullError);
        setShowErrorAlert(true);

        return fullError;
    }, []);

    const clearError = useCallback(() => {
        setLocalError(null);
        setShowErrorAlert(false);
    }, []);

    const getErrorDisplay = useCallback(() => {
        if (!localError) return props.error ? {
            message: props.error,
            isFromProps: true
        } : null;

        return {
            message: localError.message,
            details: localError.details,
            context: localError.context,
            code: localError.code,
            isFromProps: false
        };
    }, [localError, props.error]);

    const canPaymentTypeExceedBalance = useCallback((tipoPago) => {
        return EXCEED_BALANCE_ALLOWED_TYPES.includes(tipoPago);
    }, []);

    const validatePaymentAmount = useCallback((tipoPago) => {
        if (saldoRestante <= 0 && !canPaymentTypeExceedBalance(tipoPago)) {
            const tipoTexto = PAYMENT_METHODS_TEXT[tipoPago] || 'este tipo';
            notificarMsg(
                `No se puede agregar un pago de ${tipoTexto} porque el monto ya está cubierto y este método no permite vuelto.`,
                'warning'
            );
            return false;
        }
        return true;
    }, [saldoRestante, canPaymentTypeExceedBalance]);

    // Efectos
    useEffect(() => {
        const initializeData = async () => {
            try {
                const [codigos, empresaConf] = await Promise.all([
                    fetchCodigosValidacion(),
                    fetchConfEmpresa()
                ]);

                setCodigosValidacionUsuario(codigos);
                setConfEmpresa(empresaConf);
            } catch (error) {
                console.error("Error initializing data:", error);
            }
        };

        initializeData();
    }, [fetchCodigosValidacion, fetchConfEmpresa]);

    useEffect(() => {
        if (showModalWhatsApp && whatsAppInputRef.current) {
            setTimeout(() => {
                whatsAppInputRef.current.focus();
            }, 100);
        }
    }, [showModalWhatsApp]);

    // Handlers
    const openModalWhatsApp = useCallback(() => {
        const rawPhone = (props.Venta && props.Venta.Celular) || props.preventa.Celular || "";
        let cleanNumber = String(rawPhone).replace(/\D/g, "");

        if (cleanNumber.startsWith("51") && cleanNumber.length > 9) {
            cleanNumber = cleanNumber.substring(2);
        }

        if (cleanNumber.length > 9) {
            cleanNumber = cleanNumber.slice(-9);
        }

        setWhatsAppPlaceholder(cleanNumber.length === 9 ? cleanNumber : "Ingrese 9 dígitos");
        setWhatsAppNumber("");
        setShowModalWhatsApp(true);
    }, [props.Venta, props.preventa.Celular]);

    const handleCloseModalWhatsApp = useCallback(() => {
        setShowModalWhatsApp(false);
        setWhatsAppNumber("");
        setWhatsAppPlaceholder("Ingrese 9 dígitos");
    }, []);

    const sendWhatsApp = useCallback(async () => {
        const numeroDestino = (whatsAppNumber || whatsAppPlaceholder || "").trim();

        if (!numeroDestino || numeroDestino.length !== 9) {
            notificarMsg("Por favor, ingrese un número de WhatsApp válido (9 dígitos).", "warning");
            return;
        }

        setIsSendingWhatsApp(true);
        try {
            const configReq = await fetch("/api/config/whatsapp");
            if (!configReq.ok) {
                const errorText = await configReq.text();
                throw new Error(`No se pudo obtener la configuración de WhatsApp (HTTP ${configReq.status}). ${errorText || 'Verifique que el servidor esté disponible.'}`);
            }
            const waConfig = await configReq.json();
            const { Url, ApiKey, Instancia } = waConfig;
            const rv = props.Venta;
            const resEnvioSunat = convertStrToJson(rv.respuestaEnvioSunat);

            const codigoTipo = String(rv.CodigoTipoComprobante || '').padStart(2, '0');
            const numeroSinCeros = ((rv.NumeroComprobante || '').toString().replace(/^0+/, '') || '0');

            const oldXmlUrl = `${process.env.REACT_APP_FACT_BASE_URL}/datos/${rv.Ruc}/xml/${codigoTipo}/${rv.Ruc}-${codigoTipo}-${rv.Serie}-${numeroSinCeros}.xml`;
            const oldCdrUrl = `${process.env.REACT_APP_FACT_BASE_URL}/datos/${rv.Ruc}/xml/${codigoTipo}/R-${rv.Ruc}-${codigoTipo}-${rv.Serie}-${numeroSinCeros}.zip`;

            const xmlUrl = oldXmlUrl.replace('http//', 'https//');
            const cdrUrl = oldCdrUrl.replace('http//', 'https//');


            const response = await fetch(`${Url}/message/sendText/${Instancia}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "apikey": ApiKey
                },
                body: JSON.stringify({
                    number: `51${numeroDestino}`,
                    text: `*${rv.Empresa || ''}*
*RUC: ${rv.Ruc || ''}*
_______________________________

*📄 COMPROBANTE ELECTRÓNICO*
*🧾 ${rv.Serie || ''}-${((rv.NumeroComprobante || '').toString().replace(/^0+/, '') || '0')}*
*💰 TOTAL: ${rv.Simbolo || 'S/'}. ${decimalAdjust('floor', rv.Total, -2).toFixed(2)}*

Estimado cliente, puede gestionar sus archivos en los siguientes enlaces:

*📁 Descarga Directa:*
🔹 *XML:* ${xmlUrl}
🔹 *CDR:* ${cdrUrl}

*📑 ¿Necesitas el PDF?*
Conviértelo aquí: https://mifacturaperu.com/convertidor.php

🌐 _WWW.MIFACTURAPERU.COM_`,
                    delay: 1200,
                    linkPreview: true
                })
            });

            if (response.ok) {
                swal({
                    title: "¡Enviado!",
                    text: "El mensaje ha sido enviado correctamente por WhatsApp.",
                    icon: "success"
                });
                handleCloseModalWhatsApp();
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.message || `Error HTTP ${response.status} al enviar el mensaje`;
                throw new Error(errorMsg);
            }
        } catch (error) {
            handleError(error, "Envío de WhatsApp");
            swal({
                title: "Error al enviar WhatsApp",
                text: error.message || "No se pudo enviar el mensaje. Verifique la configuración o intente más tarde.",
                icon: "error"
            });
        } finally {
            setIsSendingWhatsApp(false);
        }
    }, [whatsAppNumber, whatsAppPlaceholder, handleCloseModalWhatsApp, handleError]);

    const handlePaymentMethodClick = useCallback((tipoPago) => {
        const methodConfig = PAYMENT_METHODS_CONFIG[tipoPago];

        if (!validatePaymentAmount(tipoPago)) {
            return;
        }

        if (methodConfig.requiresValidation) {
            checkPerm(methodConfig.permission, tipoPago);
        } else {
            props.handleTipoPago(tipoPago)();
        }
    }, [validatePaymentAmount, props.handleTipoPago]);

    const checkPerm = useCallback(async (perm, tipoPago) => {
        try {
            const req = await fetch(`/api/usuarios/${perm}/validar`);

            if (!req.ok) {
                throw new Error(`Error HTTP ${req.status}: No se pudo validar los permisos para ${PAYMENT_METHODS_TEXT[tipoPago] || 'este método'}`);
            }

            const data = await req.json();
            setModalValidacionTipoPago(tipoPago);
            setShowModalValidacion(data.codigoConfirmacion);
        } catch (e) {
            console.error("Permission check error:", e);
            handleError(e, `Validación de permisos para ${PAYMENT_METHODS_TEXT[tipoPago] || 'método de pago'}`);
            props.handleTipoPago(tipoPago)();
            setShowModalValidacion(false);
        }
    }, [props.handleTipoPago, handleError]);

    const validarCodigoUsuario = useCallback(() => {
        const validCode = codigosValidacionUsuario.some(c => c === typedCodigoUsuario);

        if (validCode) {
            props.handleTipoPago(modalValidacionTipoPago)();
            setShowModalValidacion(false);
            notificarMsg("Validación completada exitosamente.");
        } else {
            notificarMsg('El código ingresado no es correcto.', 'warning');
        }
        setTypedCodigoUsuario("");
    }, [codigosValidacionUsuario, typedCodigoUsuario, modalValidacionTipoPago, props.handleTipoPago]);

    const validateMontoUpdate = useCallback(async (index, nuevoMonto, tipoPago) => {
        const montoNumerico = parseFloat(nuevoMonto) || 0;

        if (!canPaymentTypeExceedBalance(tipoPago)) {
            const montoActual = props.Pagos[index]?.Monto || 0;
            const saldoAjustado = saldoRestante + montoActual;

            if (montoNumerico > saldoAjustado) {
                const tipoTexto = PAYMENT_METHODS_TEXT[tipoPago] || 'este tipo de pago';
                notificarMsg(
                    `El monto de ${tipoTexto} no puede ser mayor a S/. ${saldoAjustado.toFixed(2)} porque este método no permite vuelto.`,
                    'warning'
                );

                await props.updateMontoPago(index, Math.max(0, saldoAjustado).toFixed(2));
                return;
            }
        }

        await props.updateMontoPago(index, nuevoMonto);
    }, [canPaymentTypeExceedBalance, saldoRestante, props.Pagos, props.updateMontoPago]);

    const handleFirstButtonClick = useCallback(() => {
        if (shouldRemovePayments) {
            alert("El orden de los medios de pago seleccionados no es válido. Verifique si todos son necesarios e inténtelo otra vez.");
            return;
        }
        setShowNuevaPreventa(true);
        props.handlePagar();
    }, [shouldRemovePayments, props.handlePagar]);

    // Render helpers
    const renderPaymentMethodsCarouselDesktop = () => {
        // Definir los grupos de métodos para el carousel desktop
        const paymentGroups = [
            // Grupo 1: Métodos principales
            [POS, EFECTIVO, YAPE],
            // Grupo 2: Tarjetas y otros
            [MASTER, AMERICAN, OTROS],
            // Grupo 3: Crédito y digitales
            [PAGO_CREDITO, PLIN, RAPPI].filter(method =>
                PAYMENT_METHODS_CONFIG[method]
            )
        ].filter(group => group.length > 0);

        return (
            <div id="carousel-menu-method-pagos" className="carousel carousel-dark slide w-100 botones-desk" data-interval="false">
                <div className="carousel-inner">
                    {paymentGroups.map((group, groupIndex) => (
                        <div key={`group-${groupIndex}`} className={`carousel-item ${groupIndex === 0 ? 'active' : ''}`}>
                            <div className="pagos__methods--container">
                                {group.map((method) => {
                                    const config = PAYMENT_METHODS_CONFIG[method];
                                    if (!config) return null;

                                    const isDisabled = method === PAGO_CREDITO
                                        ? disableAll || disableCredit
                                        : disableAll || props.creditoSelected;

                                    const additionalDisabled = method === EFECTIVO
                                        ? props.PagoEfectivoUnico
                                        : false;

                                    return (
                                        <div key={method} className="pagos__methods ml-2">
                                            <input
                                                type="image"
                                                src={config.src}
                                                className={`btn ${config.className}`}
                                                onClick={() => handlePaymentMethodClick(method)}
                                                data-toggle="tooltip"
                                                data-placement="bottom"
                                                title={config.title}
                                                disabled={isDisabled || additionalDisabled}
                                                tabIndex="1"
                                                {...(method === EFECTIVO && { autoFocus: true })}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                {paymentGroups.length > 1 && (
                    <>
                        <a className="carousel-control-prev btn-atras" href="#carousel-menu-method-pagos" role="button" data-slide="prev">
                            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span className="sr-only">Previous</span>
                        </a>
                        <a className="carousel-control-next" href="#carousel-menu-method-pagos" role="button" data-slide="next">
                            <span className="carousel-control-next-icon" aria-hidden="true"></span>
                            <span className="sr-only">Next</span>
                        </a>
                    </>
                )}
            </div>
        );
    };

    const renderPaymentMethodsCarouselMobile = () => {
        const allMethods = [
            POS, EFECTIVO, YAPE, MASTER, AMERICAN, OTROS, PAGO_CREDITO,
            PLIN, RAPPI
        ].filter(method => PAYMENT_METHODS_CONFIG[method]);

        return (
            <div id="carousel-menu-method-pagos-2" className="carousel carousel-dark slide w-100 botones-resp" data-interval="false">
                <div className="carousel-inner">
                    {allMethods.map((method, index) => {
                        const config = PAYMENT_METHODS_CONFIG[method];
                        if (!config) return null;

                        const isDisabled = method === PAGO_CREDITO
                            ? disableAll || disableCredit
                            : disableAll || props.creditoSelected;

                        const additionalDisabled = method === EFECTIVO
                            ? props.PagoEfectivoUnico
                            : false;

                        return (
                            <div key={method} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                                <div className="pagos__methods--container">
                                    <div className="pagos__methods">
                                        <input
                                            type="image"
                                            src={config.src}
                                            className={`btn ${config.className}`}
                                            onClick={() => handlePaymentMethodClick(method)}
                                            data-toggle="tooltip"
                                            data-placement="bottom"
                                            title={config.title}
                                            disabled={isDisabled || additionalDisabled}
                                            tabIndex="1"
                                            {...(method === EFECTIVO && { autoFocus: true })}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {allMethods.length > 1 && (
                    <>
                        <a className="carousel-control-prev btn-atras" href="#carousel-menu-method-pagos-2" role="button" data-slide="prev">
                            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span className="sr-only">Previous</span>
                        </a>
                        <a className="carousel-control-next" href="#carousel-menu-method-pagos-2" role="button" data-slide="next">
                            <span className="carousel-control-next-icon" aria-hidden="true"></span>
                            <span className="sr-only">Next</span>
                        </a>
                    </>
                )}
            </div>
        );
    };

    const renderTotalItems = () => {
        const items = [
            { key: 'gravado', value: props.TotalGravadas, label: 'Gravado' },
            { key: 'inafecto', value: props.TotalInafectas, label: 'Inafecto' },
            { key: 'exonerado', value: props.TotalExoneradas, label: 'Exonerado' },
            { key: 'gratuito', value: props.Gratuitas, label: 'Gratuito' },
            { key: 'icbper', value: props.TotalICBPER, label: 'ICBPER' },
            { key: 'igv', value: props.TotalIGV, label: 'IGV' },
            { key: 'redondeo', value: props.redondeo, label: 'Redondeo' }
        ];

        return items.map(item => {
            if (item.value > 0) {
                return (
                    <p key={item.key} value={item.value}>
                        <strong>
                            <span>
                                <span>{item.label}</span><br />
                                <span style={{ whiteSpace: 'nowrap' }}>
                                    S/. {decimalAdjust('floor', item.value, -2)}
                                </span>
                            </span>
                        </strong>
                    </p>
                );
            }
            return null;
        });
    };

    return (
        <>
            <div className="pagos__container" tabIndex="-1">
                <div className="row pagos">
                    <div className="preventa__container_data col-12 col-sm-6">
                        <div className="row">
                            <div className="col-12 col-sm-6 mt-3">
                                {showNuevaPreventa && (
                                    <BotonGestionPreventas
                                        to="/gestionpreventas"
                                        title="Lista de Preventas Pendientes"
                                        tabIndex="0"
                                    />
                                )}
                                {!props.isLoading && !props.hasPayed && props.permisosEditar && (
                                    <button
                                        type="button"
                                        className="boton__naranja ml-1"
                                        title="Editar Preventa"
                                        onClick={() => props.setRedirect("/preVentas/edit/" + props.IdPreventa)}
                                    >
                                        <i className="fa fa-edit" />
                                    </button>
                                )}
                            </div>
                            <div className="col-12 col-sm-6 d-flex justify-content-end preventa__cliente mt-3">
                                <input
                                    type="image"
                                    src={Cliente}
                                    data-toggle="tooltip"
                                    title="Datos del Cliente"
                                    width="40px"
                                    height="50px"
                                    tabIndex="0"
                                />
                                <div className="preventa__cliente--contenido">
                                    <p>{props.preventa.Alias}</p>
                                    <p><strong>{props.preventa.NroTipoDocumento}</strong></p>
                                    <p>{props.preventa.RazonSocial}</p>
                                    {props.preventa.Vendedor && <p className=" mt-1" style={{ fontSize: '0.85rem' }}>Vendedor: <strong>{props.preventa.Vendedor}</strong></p>}
                                </div>
                            </div>
                        </div>
                        <div className="preventa__carrito">
                            <ReactTable
                                tabIndex="0"
                                id="table-productos-to-xls"
                                data={props.detallePreventa}
                                filterable
                                defaultFilterMethod={(filter, row) => String(row[filter.id]) === filter.value}
                                columns={[
                                    {
                                        columns: [
                                            {
                                                Header: "Descripción",
                                                id: "NombreProductoConPresentacion",
                                                accessor: d => {
                                                    let nombreProducto = d.NombreProductoConPresentacion;
                                                    if (nombreProducto.includes(d.unidadMedida)) {
                                                        nombreProducto = nombreProducto.replace(d.unidadMedida, '') + ' ' + d.unidadMedida;
                                                    } else {
                                                        nombreProducto = nombreProducto + ' ' + d.unidadMedida;
                                                    }
                                                    if (nombreProducto.includes(d.NombrePresentacion)) {
                                                        nombreProducto = nombreProducto.replace(d.NombrePresentacion, '');
                                                    }
                                                    return nombreProducto;
                                                },
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, {
                                                        keys: ["NombreProductoConPresentacion"]
                                                    }),
                                                filterAll: true
                                            },
                                            {
                                                Header: "Cantidad",
                                                id: "Cantidad",
                                                accessor: d => d.Cantidad,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Cantidad"] }),
                                                filterAll: true,
                                                style: { textAlign: "right" }
                                            },
                                            {
                                                Header: "Precio",
                                                id: "PrecioVenta",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.PrecioVenta, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["PrecioVenta"] }),
                                                filterAll: true,
                                                style: { textAlign: "right" }
                                            },
                                            {
                                                Header: "Descuento",
                                                id: "Descuento",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.Descuento, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Descuento"] }),
                                                filterAll: true
                                            },
                                            {
                                                Header: "Total",
                                                id: "Total",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.Total, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Total"] }),
                                                filterAll: true
                                            }
                                        ]
                                    }
                                ]}
                                defaultPageSize={8}
                                className="-striped -highlight"
                                previousText="Anterior"
                                nextText="Siguiente"
                                noDataText="No se encontraron registros"
                                loadingText="Cargando..."
                                ofText="de"
                                rowsText="filas"
                                pageText="Página"
                                pageSizeOptions={[8, 20, 25, 50, 100]}
                            />
                        </div>
                    </div>

                    <div className="encabezado col-12 col-sm-6 encabezado-resp">
                        <div className="mt-1">PAGOS - {props.TipoModalidadPago}</div>
                        <div className="pagos__tipo--comprobante">
                            {[FACTURA, BOLETA, VALE].map((tipo, idx) => (
                                <button
                                    key={tipo}
                                    type="button"
                                    tabIndex="0"
                                    className={
                                        props.TipoComprobante === tipo
                                            ? "pagos__tipo--comprobante--item--active"
                                            : "pagos__tipo--comprobante--item"
                                    }
                                    hidden={props.TipoComprobante !== tipo}
                                    onClick={() => props.handleTipoComprobante(
                                        tipo === FACTURA ? 2 : tipo === BOLETA ? 4 : 1
                                    )}
                                >
                                    {tipo === FACTURA ? "Factura" : tipo === BOLETA ? "Boleta" : "Vale"}
                                </button>
                            ))}
                        </div>
                        <div className="detalles__pago">
                            <div className="detalles__pago__item">
                                {props.Pagos.map((pago, index) => (
                                    <DetallePago
                                        key={`pay_${index}`}
                                        IdTipoPago={pago.IdTipoPago}
                                        Monto={pago.Monto}
                                        Referencia={pago.Referencia}
                                        handleFocus={props.handleFocus}
                                        handleMontoPago={async (event) => {
                                            await validateMontoUpdate(index, event.target.value, pago.IdTipoPago);
                                        }}
                                        handleReferenciaPago={(event) => {
                                            props.updateReferenciaPago(index, event.target.value);
                                        }}
                                        handleSelectPlazosCredito={async (event) => {
                                            await props.updateFechaPago(index, event.target.value);
                                        }}
                                        PlazoCredito={pago.PlazoCredito}
                                        handlePagoEnterKeyUp={props.handlePagoEnterKeyUp}
                                        handleRemovePago={() => props.removePago(index)}
                                        fechaPago={pago.FechaPago}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="pagos__methods--container w-100">
                            {renderPaymentMethodsCarouselDesktop()}
                            {renderPaymentMethodsCarouselMobile()}
                        </div>

                        <div className="info__input__container">
                            <div className="input__container">
                                <label>Vuelto</label>
                                <input
                                    style={{ overflow: "hidden" }}
                                    className="input"
                                    tabIndex="0"
                                    aria-label="Soles"
                                    placeholder="0.00"
                                    readOnly
                                    value={vueltoCalculado}
                                />
                            </div>
                            <div className="input__container">
                                <label>Observaciones</label>
                                <input
                                    style={{ overflow: "hidden" }}
                                    className="input"
                                    aria-label="Observaciones"
                                    placeholder=""
                                    value={props.Observaciones}
                                    onChange={props.handleObservaciones}
                                    tabIndex="0"
                                />
                            </div>
                            <div className="input__container d-none d-lg-flex justify-content-between align-items-center">
                                <label>Orden Compra</label>
                                <input
                                    style={{ overflow: "hidden" }}
                                    className="input"
                                    aria-label="Observaciones"
                                    placeholder=""
                                    tabIndex="0"
                                    value={props.OrdenCompra}
                                    onChange={props.handleOrdenCompra}
                                />
                            </div>
                            <div className="w-100 mt-2">
                                <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center">
                                    <div className="flex-grow-1 ml-3 mr-3">
                                        <GuiaRemisionSelect TipoComprobante={props.TipoComprobante} Venta={props.Venta} pagado={props.BotonPagado} onChange={props.handleGuiaRemision} />
                                    </div>

                                    {props.BotonPagado && <div className="d-flex flex-column flex-sm-row mt-2 mt-lg-0">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const params = props.TipoComprobante === VALE
                                                    ? `?idVale=${props.Venta.IdVale}`
                                                    : `?idVenta=${props.Venta.IdRegistroVenta}&idTipoDoc=${props.TipoComprobante}`;
                                                window.location.href = `/GuiaRemision${params}`;
                                            }}
                                            className="btn btn-info ml-lg-2 mb-2 mb-sm-0"
                                            style={{ borderRadius: "10px", height: "100%" }}
                                        >
                                            <i className="fas fa-truck mr-1" /> Guía
                                        </button>
                                        <button
                                            type="button"
                                            onClick={openModalWhatsApp}
                                            className="btn btn-success ml-sm-2 ml-lg-2"
                                            tabIndex="6"
                                            style={{ backgroundColor: "#075e54", borderRadius: "10px", height: "100%", borderRadius: "15px" }}
                                        >
                                            <i className="fab fa-whatsapp mr-1" /> Enviar
                                        </button>
                                    </div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="totals__container row">
                    <div className="totals__container--item col-12 col-sm-6">
                        {renderTotalItems()}
                    </div>

                    <div className="totals__container--item-total col-12 col-sm-2 total-resp">
                        <span style={{ display: "block", margin: "auto" }}>
                            {"S/" + props.Total.toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                    </div>

                    <div className="totals__container--item col-12 col-sm-4 no-pad d-flex justify-content-between">
                        <p style={{ margin: "0 0 0 1rem" }}>
                            <strong>
                                {saldoRestante > 0 &&
                                    `Pendiente S/${saldoRestante.toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}`
                                }
                            </strong>
                        </p>

                        {props.isLoading ? "Cargando" : getErrorDisplay() ? (
                            <button
                                className="btn btn-outline-danger"
                                onClick={() => setShowErrorAlert(true)}
                                title="Ver detalles del error"
                                style={{ height: "100%" }}
                            >
                                ⚠️ Error - Ver
                            </button>
                        ) : (
                            props.hasPayed ? (
                                <BotonNuevaPreventa
                                    to="/pre-invoice/add"
                                    title="Lista de Preventas Pendientes"
                                    tabIndex="0"
                                    style={{ height: "100%" }}
                                />
                            ) : (
                                <button
                                    className="pagos__boton"
                                    onClick={handleFirstButtonClick}
                                    tabIndex="3"
                                    hidden={!props.detallePreventa.length}
                                    disabled={!(props.TotalPagado >= props.Total && props.ClickPagar)}
                                >
                                    PAGAR
                                </button>
                            )
                        )}

                        {props.Venta && props.hasPayed && (
                            <>
                                <div className="botones-desk d-none d-md-flex">
                                    <button
                                        className="boton__verdeClaro--letras ml-1 ml-5"
                                        onClick={() => props.printTicketVenta(props.Venta, props.DetallesVenta)}
                                        tabIndex="4"
                                        style={{ height: "100%", borderRadius: "15px" }}
                                    >
                                        Ticket <i className="fas fa-print" />
                                    </button>
                                    <button
                                        onClick={() => printA4(props.DetallesVenta, props.Venta, props.extImg, props.cuentasBancarias)}
                                        className="boton__verdeOscuro--letras ml-1"
                                        tabIndex="5"
                                        style={{ height: "100%", borderRadius: "15px" }}
                                    >
                                        A4 <i className="fas fa-print" />
                                    </button>
                                </div>
                                <div className="botones-resp d-flex d-md-none">
                                    <button
                                        onClick={() => downprintA4(props.DetallesVenta, props.Venta, props.extImg)}
                                        className="boton__verdeOscuro--letras ml-1"
                                        tabIndex="5"
                                    >
                                        A4 <i className="fas fa-print" />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={showModalValidacion}
                onClose={() => setShowModalValidacion(false)}
                width='900px'
                title="Validar venta crédito"
            >
                <label className="font-weight-bold" htmlFor="inputValidacion">
                    Código
                </label>
                <input
                    type="password"
                    name="inputValidacion"
                    className="form-control mb-2"
                    value={typedCodigoUsuario}
                    onChange={e => setTypedCodigoUsuario(e.target.value)}
                />

                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-verde-mfp-letra-normal"
                        title="Validar"
                        onClick={validarCodigoUsuario}
                    >
                        Validar
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showModalWhatsApp}
                onClose={handleCloseModalWhatsApp}
                width='400px'
                title="Enviar por WhatsApp"
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!isSendingWhatsApp) {
                            sendWhatsApp();
                        }
                    }}
                >
                    <div className="form-group">
                        <label className="font-weight-bold">
                            Número de WhatsApp
                        </label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">+51</span>
                            </div>
                            <input
                                ref={whatsAppInputRef}
                                type="text"
                                className="form-control"
                                placeholder={whatsAppPlaceholder}
                                maxLength="9"
                                value={whatsAppNumber}
                                onChange={e => setWhatsAppNumber(e.target.value.replace(/\D/g, ""))}
                            />
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-3">
                        <button
                            type="submit"
                            className="btn btn-verde-mfp-letra-normal"
                            title="Enviar"
                            disabled={isSendingWhatsApp}
                        >
                            {isSendingWhatsApp ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2" /> Enviando...
                                </>
                            ) : (
                                "Enviar"
                            )}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Modal de Error Detallado */}
            <Modal
                isOpen={showErrorAlert}
                onClose={clearError}
                width='600px'
                title="[!] Reporte de Error"
            >
                {getErrorDisplay() && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                            <div style={{ fontWeight: '600', color: '#495057', fontSize: '0.9rem', textTransform: 'uppercase', marginBottom: '5px' }}>
                                Mensaje Principal
                            </div>
                            <div style={{
                                color: '#dc3545',
                                fontSize: '1rem',
                                wordBreak: 'break-word',
                                padding: '8px 12px',
                                backgroundColor: '#f8f9fa',
                                borderLeft: '3px solid #dc3545',
                                borderRadius: '4px',
                                fontFamily: 'monospace'
                            }}>
                                {getErrorDisplay().message}
                            </div>
                        </div>

                        {getErrorDisplay().context && (
                            <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                                <div style={{ fontWeight: '600', color: '#495057', fontSize: '0.9rem', textTransform: 'uppercase', marginBottom: '5px' }}>
                                    Contexto
                                </div>
                                <div style={{
                                    color: '#856404',
                                    fontSize: '1rem',
                                    wordBreak: 'break-word',
                                    padding: '8px 12px',
                                    backgroundColor: '#fff3cd',
                                    borderLeft: '3px solid #ffc107',
                                    borderRadius: '4px'
                                }}>
                                    {getErrorDisplay().context}
                                </div>
                            </div>
                        )}

                        {getErrorDisplay().code && getErrorDisplay().code !== 'UNKNOWN' && (
                            <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                                <div style={{ fontWeight: '600', color: '#495057', fontSize: '0.9rem', textTransform: 'uppercase', marginBottom: '5px' }}>
                                    Codigo de Error
                                </div>
                                <div style={{
                                    color: '#495057',
                                    fontSize: '1rem',
                                    wordBreak: 'break-word',
                                    padding: '8px 12px',
                                    backgroundColor: '#f1f3f5',
                                    borderLeft: '3px solid #6c757d',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace'
                                }}>
                                    {getErrorDisplay().code}
                                </div>
                            </div>
                        )}

                        {getErrorDisplay().details && (
                            <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                                <div style={{ fontWeight: '600', color: '#495057', fontSize: '0.9rem', textTransform: 'uppercase', marginBottom: '5px' }}>
                                    Detalles Tecnicos
                                </div>
                                <div style={{
                                    color: '#6c757d',
                                    fontSize: '0.85rem',
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    padding: '8px 12px',
                                    backgroundColor: '#f8f9fa',
                                    borderLeft: '3px solid #6c757d',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace'
                                }}>
                                    {getErrorDisplay().details}
                                </div>
                            </div>
                        )}

                        <div style={{ borderBottom: 'none' }}>
                            <div style={{ fontWeight: '600', color: '#495057', fontSize: '0.9rem', textTransform: 'uppercase', marginBottom: '5px' }}>
                                Informacion Adicional
                            </div>
                            <p style={{ margin: '5px 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                <strong>Acciones recomendadas:</strong>
                            </p>
                            <ul style={{ marginBottom: 0, paddingLeft: '20px', color: '#6c757d', fontSize: '0.9rem' }}>
                                <li>Verifique su conexion a internet</li>
                                <li>Intente la operacion nuevamente</li>
                                <li>Si el problema persiste, contacte al administrador</li>
                                <li>Proporcione el codigo de error al reportar</li>
                            </ul>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-between mt-3">
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            const errorReport = getErrorDisplay();
                            const lines = [
                                `Error: ${errorReport.message}`,
                                `Contexto: ${errorReport.context || 'N/A'}`,
                                `Codigo: ${errorReport.code || 'UNKNOWN'}`
                            ];
                            const text = lines.join('\n');
                            navigator.clipboard.writeText(text).catch(err => console.error('Error:', err));
                            notificarMsg('Error copiado al portapapeles', 'success');
                        }}
                        title="Copiar detalles del error"
                    >
                        <i className="fas fa-copy mr-2" /> Copiar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={clearError}
                        title="Cerrar"
                    >
                        Entendido
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default PagosUI;